import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, Buildings, DownloadSimple, WarningCircle } from '@phosphor-icons/react';
import { fetchRevenuePrediction } from '../../api/revenuePrediction';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { DataSourceChip } from '../../components/journeys/DataSourceChip';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAppNavigation } from '../../context/AppNavigationContext';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import { usePersistedState } from '../../hooks/usePersistedState';
import { LIVE_DATA_SOURCE_LABEL } from '../../lib/journeyApiFilters';
import { cn } from '../../lib/utils';

const PREFS_KEY = 'vi.revenuePrediction.prefs.v1';

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function oneYearAgo(date) {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() - 1);
  return next;
}

const TODAY = new Date();
const DEFAULT_FILTERS = {
  targetStart: isoDate(TODAY),
  targetEnd: isoDate(addDays(TODAY, 6)),
  lookbackStart: isoDate(oneYearAgo(TODAY)),
  lookbackEnd: isoDate(addDays(TODAY, -1)),
};

const confidenceTone = {
  high: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  medium: 'bg-amber-50 text-amber-900 ring-amber-200/80',
  low: 'bg-orange-50 text-orange-900 ring-orange-200/80',
  unavailable: 'bg-slate-50 text-slate-600 ring-slate-200',
};

function formatNumber(value) {
  return new Intl.NumberFormat('da-DK').format(Number(value || 0));
}

function formatMoney(value, currencyCode) {
  return `${currencyCode || 'DKK'} ${formatNumber(Math.round(Number(value || 0)))}`;
}

function FilterField({ label, children }) {
  return (
    <label className="flex min-w-[9.5rem] flex-1 basis-[9.5rem] flex-col gap-1 sm:basis-[11rem]">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

const filterControlClass =
  'h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 shadow-sm outline-none transition hover:bg-slate-50 focus:border-[var(--brand-primary,#1A428A)]/40 focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15';

function ConfidenceBadge({ level }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[4.5rem] items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ring-1',
        confidenceTone[level] || confidenceTone.unavailable
      )}
    >
      {level || 'unavailable'}
    </span>
  );
}

function friendlyPredictionError(rawMessage) {
  const message = String(rawMessage || '').toLowerCase();
  if (message.includes('accountid') || message.includes('account id')) {
    return {
      variant: 'info',
      icon: Buildings,
      title: 'Select a tenant first',
      message:
        'Open a customer account from Tenants so we know whose revenue to predict.',
    };
  }
  return {
    variant: 'error',
    icon: WarningCircle,
    title: "We couldn't load the prediction",
    message: 'Something went wrong while building this forecast. Try refreshing or adjust the date range.',
  };
}

function exportPrediction(data) {
  if (!data) return;
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `venue-intelligence-revenue-prediction-${data.targetPeriod?.start || 'export'}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function RevenuePredictionPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const { setHeaderTrailing } = useAppNavigation();
  const [filters, setFilters] = usePersistedState(PREFS_KEY, DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...filters }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;
  const needsTenant = Boolean(isSuperAdmin && !viewingTenant);

  const loadPrediction = useCallback(
    async (nextFilters = filters) => {
      if (isSuperAdmin && !accountId) {
        setLoading(false);
        setData(null);
        setError('NEEDS_TENANT');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchRevenuePrediction({
          ...nextFilters,
          accountId,
        });
        setData(response);
      } catch (err) {
        setError(err?.message || 'Failed to load revenue prediction');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [accountId, filters, isSuperAdmin]
  );

  useEffect(() => {
    loadPrediction(filters);
  }, [
    filters.targetStart,
    filters.targetEnd,
    filters.lookbackStart,
    filters.lookbackEnd,
    accountId,
    loadPrediction,
    filters,
  ]);

  useEffect(() => {
    setHeaderTrailing(
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => loadPrediction(filters)}
          className="h-9 gap-1.5 !rounded-full"
        >
          <ArrowClockwise size={14} weight="bold" aria-hidden="true" />
          Refresh
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => exportPrediction(data)}
          disabled={!data}
          className="h-9 gap-1.5 !rounded-full"
        >
          <DownloadSimple size={14} weight="bold" aria-hidden="true" />
          Export
        </Button>
      </div>
    );
    return () => setHeaderTrailing(null);
  }, [data, filters, loadPrediction, setHeaderTrailing]);

  const prediction = data?.predictions?.[0] || null;
  const currencyCode = prediction?.currencyCode || 'DKK';
  const variance = Number(prediction?.variance || 0);
  const dailyRows = useMemo(() => prediction?.dailyPredictions || [], [prediction]);
  const sourceBreakdown = prediction?.sourceBreakdown || [];
  const maxDailyRevenue = Math.max(
    ...dailyRows.map((row) =>
      Math.max(Number(row.predictedRevenue || 0), Number(row.actualRevenue || 0))
    ),
    1
  );

  const summaryStats = useMemo(() => {
    if (!prediction) return [];

    const varianceTrend =
      prediction.variance == null ? 'neutral' : variance >= 0 ? 'up' : 'down';

    return [
      {
        title: 'Expected revenue',
        value: formatMoney(prediction.predictedRevenue, currencyCode),
        change: '—',
        trend: 'neutral',
        context:
          data?.targetPeriod?.start && data?.targetPeriod?.end
            ? `${data.targetPeriod.start} → ${data.targetPeriod.end}`
            : 'Target window',
        featured: true,
      },
      {
        title: 'Actual revenue',
        value:
          prediction.actualRevenue == null
            ? 'Pending'
            : formatMoney(prediction.actualRevenue, currencyCode),
        change: '—',
        trend: 'neutral',
        context: prediction.actualRevenue == null ? 'No target data yet' : 'In selected window',
      },
      {
        title: 'Variance',
        value:
          prediction.variance == null
            ? 'Pending'
            : formatMoney(prediction.variance, currencyCode),
        change:
          prediction.variancePercent == null ? '—' : `${prediction.variancePercent}%`,
        trend: varianceTrend,
        context: prediction.variance == null ? 'Waiting for actuals' : 'vs expected',
      },
      {
        title: 'Confidence',
        value: String(prediction.confidenceLevel || '—'),
        change: '—',
        trend: 'neutral',
        context: `${prediction.historicalDays || 0} historical days`,
      },
    ];
  }, [prediction, currencyCode, data?.targetPeriod, variance]);

  function handleDraftChange(key, value) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function handleApply() {
    setFilters(draftFilters);
  }

  const methodLabel = data?.method ? String(data.method).replaceAll('_', ' ') : null;
  const topNote = (data?.notes || []).find(Boolean);

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      {loading ? (
        <PageSummaryStats stats={[]} isFirstLoad />
      ) : !error && !needsTenant ? (
        <PageSummaryStats
          stats={summaryStats}
          isFirstLoad={false}
          emptyMessage="No prediction for this lookback window. Try a wider date range."
        />
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-[#F3F5F8] px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Target start">
            <input
              type="date"
              value={draftFilters.targetStart}
              onChange={(event) => handleDraftChange('targetStart', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Target end">
            <input
              type="date"
              value={draftFilters.targetEnd}
              onChange={(event) => handleDraftChange('targetEnd', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Lookback start">
            <input
              type="date"
              value={draftFilters.lookbackStart}
              onChange={(event) => handleDraftChange('lookbackStart', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Lookback end">
            <input
              type="date"
              value={draftFilters.lookbackEnd}
              onChange={(event) => handleDraftChange('lookbackEnd', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>

          <DataSourceChip label={LIVE_DATA_SOURCE_LABEL} className="h-9 shrink-0 self-end" />

          <Button
            type="button"
            size="sm"
            onClick={handleApply}
            className="h-9 shrink-0 self-end !rounded-2xl bg-[var(--brand-primary,#1A428A)] hover:bg-[#163872] sm:min-w-[88px]"
          >
            Apply
          </Button>
        </div>
      </div>

      {!loading && (needsTenant || error) ? (
        <PanelStateMessage
          {...(needsTenant || error === 'NEEDS_TENANT'
            ? {
                variant: 'info',
                icon: Buildings,
                title: 'Select a tenant first',
                message:
                  'Open a customer account from Tenants so we know whose revenue to predict.',
              }
            : friendlyPredictionError(error))}
        />
      ) : null}

      {!loading && !error && !needsTenant && !prediction ? (
        <PanelStateMessage
          title="No prediction available"
          message="No historical revenue matched this lookback period."
        />
      ) : null}

      {!loading && !error && !needsTenant && prediction ? (
        <>
          {topNote || methodLabel ? (
            <ConfidenceStrip
              tone="success"
              title={
                methodLabel
                  ? `Baseline: ${methodLabel}${topNote ? ` · ${topNote}` : ''}`
                  : topNote
              }
            />
          ) : null}

          <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(260px,0.7fr)]">
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="px-4 py-3">
                <CardTitle>Daily forecast</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-[#F7F9FC] text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Expected</th>
                      <th className="px-4 py-3">Actual</th>
                      <th className="px-4 py-3">Variance</th>
                      <th className="px-4 py-3">Confidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dailyRows.map((row) => (
                      <tr key={row.date} className="text-slate-700 hover:bg-slate-50/80">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#201C35]">{row.date}</div>
                          <div className="text-[11px] text-slate-400">{row.weekday}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex min-w-[160px] items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full rounded-full bg-[var(--brand-primary,#1A428A)]"
                                style={{
                                  width: `${(Number(row.predictedRevenue || 0) / maxDailyRevenue) * 100}%`,
                                }}
                              />
                            </div>
                            <span className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums">
                              {formatMoney(row.predictedRevenue, currencyCode)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.actualRevenue == null ? (
                            <span className="text-xs text-slate-400">Pending</span>
                          ) : (
                            formatMoney(row.actualRevenue, currencyCode)
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.variance == null ? (
                            <span className="text-xs text-slate-400">Pending</span>
                          ) : (
                            <span
                              className={
                                row.variance >= 0 ? 'text-emerald-700' : 'text-rose-700'
                              }
                            >
                              {formatMoney(row.variance, currencyCode)}
                              {row.variancePercent != null ? (
                                <span className="text-slate-400"> ({row.variancePercent}%)</span>
                              ) : null}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <ConfidenceBadge level={row.confidenceLevel} />
                            <span className="text-[11px] tabular-nums text-slate-500">
                              {row.historicalSampleDays} days
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="px-4 py-3">
                <CardTitle>By source</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sourceBreakdown.length ? (
                  sourceBreakdown.map((source) => (
                    <div
                      key={source.source}
                      className="flex items-center justify-between gap-3 rounded-lg bg-[#F7F9FC] px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-[#201C35]">
                          {source.source}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {source.actualRevenue == null
                            ? 'Actual pending'
                            : `Actual ${formatMoney(source.actualRevenue, currencyCode)}`}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold tabular-nums text-slate-800">
                          {formatMoney(source.predictedRevenue, currencyCode)}
                        </div>
                        {source.variance != null ? (
                          <div
                            className={cn(
                              'text-[11px] tabular-nums',
                              source.variance >= 0 ? 'text-emerald-700' : 'text-rose-700'
                            )}
                          >
                            {formatMoney(source.variance, currencyCode)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-500">No source split for this window.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </section>
  );
}
